import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AssistantFab from "@/components/assistant/AssistantFab";
import AideFab from "@/components/assistant/AideFab";
import {
  LayoutDashboard, Users, Calendar, Wrench, Car, FileText, CircleDot,
  Receipt, CreditCard, BookOpen, Settings, Search, LogOut, Wallet, Banknote,
  Wrench as WrenchIcon, Menu, X, Inbox, Mail, MessageCircle, Cloud, BarChart3, Clock,
  Sparkles, AlertTriangle, Info, PanelLeftClose, PanelLeftOpen,
  Image as ImageIcon, ClipboardList, HardHat, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useGuardState } from "@/components/UnsavedChangesGuard";
import api from "@/lib/api";

const ROLE_LABELS = {
  admin: "Administrateur",
  comptoir: "Comptoir",
  atelier: "Atelier",
  compta: "Comptabilité",
};

const NAV_ITEMS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, exact: true, roles: ["admin", "comptoir", "atelier", "compta"] },
  { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "comptoir", "atelier", "compta"] },
  { to: "/calendrier", label: "Calendrier", icon: Calendar, roles: ["comptoir", "atelier"], perm: "calendrier" },
  { to: "/mail", label: "Boîte mail", icon: Mail, roles: ["admin", "comptoir", "compta"], desktopOnly: true },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, roles: ["admin", "comptoir"], desktopOnly: true },
  { to: "/atelier", label: "Atelier", icon: Wrench, roles: ["admin", "atelier", "comptoir"], perm: "atelier" },
  { to: "/atelier/mon-poste", label: "Mon poste", icon: ClipboardList, roles: ["atelier"] },
  { to: "/atelier/stats", label: "Stats atelier", icon: BarChart3, roles: ["admin"] },
  { to: "/atelier/mon-poste", label: "Vue collaborateur", icon: HardHat, roles: ["admin"] },
  { to: "/photos", label: "Photos", icon: ImageIcon, roles: ["admin", "comptoir", "atelier", "compta"] },
  { to: "/gardiennage", label: "Gardiennage pneus", icon: CircleDot, roles: ["admin", "comptoir", "atelier", "compta"] },
  { to: "/remplacement", label: "Voiture de prêt", icon: Car, roles: ["admin", "comptoir", "atelier"] },
  { to: "/facturation", label: "Facturation", icon: FileText, roles: ["admin", "comptoir", "compta"] },
  { to: "/factures-fournisseurs", label: "Factures fournisseurs", icon: Receipt, roles: ["admin", "comptoir", "compta"] },
  { to: "/depenses-rapides", label: "Dépenses rapides", icon: Wallet, roles: ["admin", "comptoir", "compta"] },
  { to: "/comptabilite", label: "Comptabilité", icon: BookOpen, roles: ["admin", "comptoir", "compta"], desktopOnly: true },
  { to: "/caisse", label: "Caisse", icon: CreditCard, roles: ["admin", "comptoir", "compta", "atelier"] },
  { to: "/pointage", label: "Pointage", icon: Clock, roles: ["admin", "comptoir", "atelier", "compta"], hideForLogins: ["admin", "comptoir", "compta", "claude", "atelier"] },
  { to: "/pointage/admin", label: "Pointage", icon: Clock, roles: ["admin"], desktopOnly: true },
  { to: "/salaires", label: "Salaires & RH", icon: Banknote, roles: ["admin"], desktopOnly: true },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isDirty, safeNavigate } = useGuardState();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ clients: [], vehicules: [], ors: [], factures: [], devis: [] });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [suiviUnread, setSuiviUnread] = useState(0);   // iter240 — messages clients non lus
  // Task #5 — Assistant Claude visible : alertes proactives (digest déterministe)
  const [assistantAlerts, setAssistantAlerts] = useState([]);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // iter240 — Poll badge messages clients non lus (10s)
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/suivi/messages/non-lus");
        setSuiviUnread(data?.total || 0);
      } catch (e) { /* silent */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => clearInterval(t);
  }, [user]);

  // Task #5 — Poll alertes assistant (30s, digest léger côté backend)
  // iter275 — réservé à l'admin (comme le chatbot)
  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get("/assistant/alerts");
        setAssistantAlerts(data?.alerts || []);
      } catch (e) { /* silent */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 30000);
    return () => clearInterval(t);
  }, [user]);

  const assistantCount = assistantAlerts.reduce((n, a) => n + (a.items?.length || 1), 0);

  // iter358 — Alerte « l'atelier pose une question » : visible partout, sans
  // attendre d'ouvrir le module Atelier.
  const [demandes, setDemandes] = useState([]);
  const [demandesOpen, setDemandesOpen] = useState(false);
  const demandesVues = useRef(null);

  useEffect(() => {
    if (!["admin", "comptoir"].includes(user?.role)) return;
    const fetchDemandes = async () => {
      try {
        const { data } = await api.get("/atelier/demandes", { params: { statut: "ouverte" } });
        const items = (data?.items || []).filter((d) => d.statut === "ouverte");
        setDemandes(items);
        const ids = new Set(items.map((d) => d.id));
        if (demandesVues.current) {
          const nouvelles = items.filter((d) => !demandesVues.current.has(d.id));
          nouvelles.slice(0, 3).forEach((d) => toast.info(
            `${d.demandeur_nom || "L'atelier"} — ${d.or?.numero || "dossier"}`,
            { description: d.texte || "Demande de l'atelier", duration: 10000 },
          ));
        }
        demandesVues.current = ids;
      } catch (e) { /* silent */ }
    };
    fetchDemandes();
    const t = setInterval(fetchDemandes, 20000);
    return () => clearInterval(t);
  }, [user]);

  // iter367 — Alerte « un client a envoyé une demande » depuis son espace de suivi.
  const [demandesClient, setDemandesClient] = useState([]);
  const clientVues = useRef(null);

  useEffect(() => {
    if (!["admin", "comptoir"].includes(user?.role)) return;
    const fetchClient = async () => {
      try {
        const { data } = await api.get("/rdv-demandes", { params: { statut: "nouveau" } });
        const items = (data?.items || data || []).filter((d) => d.statut === "nouveau");
        setDemandesClient(items);
        const ids = new Set(items.map((d) => d.id));
        if (clientVues.current) {
          items.filter((d) => !clientVues.current.has(d.id)).slice(0, 3).forEach((d) => toast.info(
            `${[d.prenom, d.nom].filter(Boolean).join(" ") || "Un client"} — ${d.nature === "devis" ? "demande de devis" : "demande de rendez-vous"}`,
            { description: d.message || d.plaque || "", duration: 10000 },
          ));
        }
        clientVues.current = ids;
      } catch (e) { /* silent */ }
    };
    fetchClient();
    const t = setInterval(fetchClient, 20000);
    return () => clearInterval(t);
  }, [user]);

  // Intercepte les clics NavLink quand isDirty=true → délégué au safeNavigate (qui ouvre la dialog)
  const handleNavClick = (to) => (e) => {
    setMobileNavOpen(false);
    if (isDirty) {
      e.preventDefault();
      safeNavigate(to);
    }
  };

  // CMD+K shortcut + raccourci « Plaque » du tableau de bord
  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    const ouvrir = () => setSearchOpen(true);
    document.addEventListener("keydown", down);
    window.addEventListener("crm:ouvrir-recherche", ouvrir);
    return () => {
      document.removeEventListener("keydown", down);
      window.removeEventListener("crm:ouvrir-recherche", ouvrir);
    };
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1) {
      setSearchResults({ clients: [], vehicules: [], ors: [], factures: [], devis: [] });
      return;
    }
    try {
      const { data } = await api.get("/search", { params: { q } });
      setSearchResults(data);
    } catch (e) {
      setSearchResults({ clients: [], vehicules: [], ors: [], factures: [], devis: [] });
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery, doSearch]);

  const visibleNav = NAV_ITEMS.filter((n) => {
    if (!n.roles.includes(user?.role)) return false;
    if (n.hideForLogins && n.hideForLogins.includes((user?.login || "").toLowerCase())) return false;
    // iter364 — Un collaborateur ne voit « Atelier » / « Calendrier » que si
    // l'administration le lui a explicitement autorisé (Paramètres → Utilisateurs).
    // « Mon poste » reste toujours visible.
    if (n.perm && user?.role === "atelier" && !(user?.permissions_atelier || []).includes(n.perm)) return false;
    return true;
  });

  const goToClient = (id) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(`/clients/${id}`);
  };

  // iter259 — barre latérale rétractable sur ordinateur (préférence mémorisée)
  const [navCollapsed, setNavCollapsed] = useState(
    () => localStorage.getItem("cm_nav_collapsed") === "1",
  );
  const toggleNav = () => {
    setNavCollapsed((v) => {
      localStorage.setItem("cm_nav_collapsed", v ? "0" : "1");
      return !v;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#fafbfa]" data-testid="app-shell">
      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          fixed lg:relative z-50 h-full bg-white border-r border-gray-200
          ${navCollapsed ? "w-64 lg:w-[68px]" : "w-64"}
          transition-all duration-200 flex flex-col`}
        data-testid="sidebar"
        data-collapsed={navCollapsed ? "1" : "0"}
      >
        <div className={`h-16 border-b border-gray-200 flex items-center gap-2 ${navCollapsed ? "px-3 lg:justify-center" : "px-5"}`}>
          <div className="w-8 h-8 rounded-md bg-[#52a675] flex items-center justify-center flex-shrink-0">
            <WrenchIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className={`leading-tight ${navCollapsed ? "lg:hidden" : ""}`}>
            <div className="font-heading font-bold text-base text-gray-900">CarMaintenance</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Meyrin</div>
          </div>
          <button
            className="ml-auto lg:hidden text-gray-500"
            onClick={() => setMobileNavOpen(false)}
            data-testid="sidebar-close-mobile"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <div
                  key={item.label}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-disabled`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className={navCollapsed ? "lg:hidden" : ""}>{item.label}</span>
                  <Badge variant="outline" className={`ml-auto text-[9px] bg-gray-50 border-gray-200 px-1.5 ${navCollapsed ? "lg:hidden" : ""}`}>
                    Bientôt
                  </Badge>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={handleNavClick(item.to)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  /* iter250 — desktopOnly : entrées inutilisables sur téléphone, masquées en < md */
                  `${item.desktopOnly ? "hidden md:flex" : "flex"} items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${navCollapsed ? "lg:justify-center lg:px-2" : ""} ${
                    isActive
                      ? "bg-[#52a675]/10 text-[#3d8a5c] font-semibold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className={navCollapsed ? "lg:hidden" : ""}>{item.label}</span>
                {item.to === "/atelier" && suiviUnread > 0 && (
                  <Badge className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0" data-testid="nav-suivi-unread">
                    {suiviUnread}
                  </Badge>
                )}
              </NavLink>
            );
          })}

          {/* Paramètres - visible pour tous */}
          <div className="pt-3 mt-3 border-t border-gray-100">
            <NavLink
              to="/parametres"
              onClick={handleNavClick("/parametres")}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${navCollapsed ? "lg:justify-center lg:px-2" : ""} ${
                  isActive
                    ? "bg-[#52a675]/10 text-[#3d8a5c] font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
              data-testid="nav-parametres"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              <span className={navCollapsed ? "lg:hidden" : ""}>Paramètres</span>
            </NavLink>
          </div>
        </nav>

        <div className="border-t border-gray-200">
          <button
            type="button"
            onClick={toggleNav}
            title={navCollapsed ? "Déplier le menu" : "Replier le menu"}
            className="hidden lg:flex w-full items-center gap-2 px-3 py-2.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
            data-testid="sidebar-toggle"
          >
            {navCollapsed
              ? <PanelLeftOpen className="w-4 h-4 mx-auto" />
              : <><PanelLeftClose className="w-4 h-4" /> Replier le menu</>}
          </button>
          <div className={`px-3 pb-3 pt-1 text-[10px] text-gray-500 ${navCollapsed ? "lg:hidden" : ""}`}>
            Phase 1 · CRM · v0.1 · 2026
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white/90 backdrop-blur-md border-b border-gray-200 flex items-center px-4 lg:px-6 gap-3 sticky top-0 z-30" data-testid="header">
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileNavOpen(true)}
            data-testid="header-mobile-menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-500 w-full max-w-xl transition-colors"
            data-testid="header-search-trigger"
          >
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">Rechercher un client, une plaque, une facture…</span>
            <span className="sm:hidden">Rechercher…</span>
            <kbd className="ml-auto hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] text-gray-400">
              <span className="text-[12px]">⌘</span>K
            </kbd>
          </button>

          <div className="ml-auto flex items-center gap-3">
            {/* iter358 — Questions de l'atelier, visibles depuis n'importe quel écran */}
            {["admin", "comptoir"].includes(user?.role) && (
              <DropdownMenu open={demandesOpen} onOpenChange={setDemandesOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-50 text-gray-600 transition-colors"
                    data-testid="header-demandes-trigger"
                    title="Questions de l'atelier"
                  >
                    <Bell className="w-5 h-5" />
                    {(demandes.length + demandesClient.length) > 0 && (
                      <Badge
                        className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full"
                        data-testid="header-demandes-count"
                      >
                        {demandes.length + demandesClient.length}
                      </Badge>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto" data-testid="demandes-dropdown">
                  {/* iter367 — Demandes des clients depuis leur espace de suivi */}
                  {demandesClient.length > 0 && (
                    <>
                      <DropdownMenuLabel className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-blue-500" /> Demandes de clients
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {demandesClient.slice(0, 8).map((d) => (
                        <DropdownMenuItem
                          key={d.id}
                          onClick={() => { setDemandesOpen(false); navigate("/demandes-rdv"); }}
                          className="flex flex-col items-start gap-0.5 py-2"
                          data-testid={`demande-client-alerte-${d.id}`}
                        >
                          <span className="text-[11px] font-semibold text-gray-700">
                            {[d.prenom, d.nom].filter(Boolean).join(" ") || d.telephone || "Client"}
                            {" · "}
                            {d.nature === "devis" ? "devis" : "rendez-vous"}
                            {d.plaque ? ` · ${d.plaque}` : ""}
                          </span>
                          <span className="text-xs text-gray-600 leading-snug line-clamp-2">
                            {d.message || "Nouvelle demande"}
                          </span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuLabel className="flex items-center gap-1.5">
                    <HardHat className="w-3.5 h-3.5 text-orange-500" /> L&apos;atelier attend une réponse
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {demandes.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-gray-400 text-center" data-testid="demandes-vide">
                      Aucune question en attente.
                    </div>
                  ) : (
                    demandes.slice(0, 8).map((d) => (
                      <DropdownMenuItem
                        key={d.id}
                        onClick={() => { setDemandesOpen(false); navigate(`/or/${d.or_id}`); }}
                        className="flex flex-col items-start gap-0.5 py-2"
                        data-testid={`demande-alerte-${d.id}`}
                      >
                        <span className="text-[11px] font-semibold text-gray-700">
                          {d.or?.numero || "Dossier"} · {d.demandeur_nom || "Atelier"}
                        </span>
                        <span className="text-xs text-gray-600 leading-snug line-clamp-2">
                          {d.texte || "Demande de l'atelier"}
                        </span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Task #5 — Assistant Claude : alertes proactives (iter275 : admin uniquement) */}
            {user?.role === "admin" && (
            <DropdownMenu open={assistantOpen} onOpenChange={setAssistantOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-50 text-gray-600 transition-colors"
                  data-testid="header-assistant-trigger"
                  title="Assistant"
                >
                  <Sparkles className="w-5 h-5" />
                  {assistantCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-[#52a675] text-white text-[10px] px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full" data-testid="header-assistant-count">
                      {assistantCount}
                    </Badge>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="assistant-dropdown">
                <DropdownMenuLabel className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#52a675]" /> Assistant — à surveiller
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {assistantAlerts.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-gray-400 text-center">Rien à signaler pour l&apos;instant.</div>
                ) : (
                  assistantAlerts.map((a) => (
                    <DropdownMenuItem
                      key={a.type}
                      onClick={() => { setAssistantOpen(false); navigate(a.url || "/"); }}
                      className="flex items-start gap-2 py-2"
                      data-testid={`assistant-alert-${a.type}`}
                    >
                      {a.severity === "warning" ? (
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      )}
                      <span className="text-xs text-gray-700 leading-snug">{a.title}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2.5 hover:bg-gray-50 rounded-md px-2 py-1.5 transition-colors"
                  data-testid="header-user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-[#52a675] text-white flex items-center justify-center text-sm font-semibold">
                    {user?.nom?.[0] || "?"}
                  </div>
                  <div className="hidden md:block text-left leading-tight">
                    <div className="text-sm font-semibold text-gray-900">{user?.nom}</div>
                    <div className="text-[11px] text-gray-500">{ROLE_LABELS[user?.role]}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.nom}</div>
                  <div className="text-xs text-gray-500 font-normal">{ROLE_LABELS[user?.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="header-parametres">
                  <Settings className="w-4 h-4 mr-2" />
                  Paramètres / mon mot de passe
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="header-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/login")} data-testid="header-switch-user">
                  Changer d'utilisateur
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Universal search dialog */}
      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput
          placeholder="Client, plaque, n° OR, facture, devis, n° de sinistre, n° d'autorisation…"
          value={searchQuery}
          onValueChange={setSearchQuery}
          data-testid="search-input"
        />
        <CommandList>
          <CommandEmpty>Aucun résultat.</CommandEmpty>
          {searchResults.clients.length > 0 && (
            <CommandGroup heading="Clients">
              {searchResults.clients.map((c) => {
                const name = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                return (
                  <CommandItem
                    key={c.id}
                    value={`client-${c.id}-${name}-${c.tel_principal || ""}-${c.email || ""}-${c.num_bexio || ""}`}
                    onSelect={() => goToClient(c.id)}
                    data-testid={`search-result-client-${c.id}`}
                  >
                    <Users className="w-4 h-4 mr-2 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-semibold">{name}</div>
                      <div className="text-xs text-gray-500">
                        {c.localite} · {c.tel_principal}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {searchResults.vehicules.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Véhicules">
                {searchResults.vehicules.map((v) => (
                  <CommandItem
                    key={v.id}
                    value={`vehicule-${v.id}-${v.plaque || ""}-${v.marque || ""}-${v.modele || ""}-${v.vin || ""}-${v.client_nom || ""}`}
                    onSelect={() => goToClient(v.client_id)}
                    data-testid={`search-result-vehicule-${v.id}`}
                  >
                    <Car className="w-4 h-4 mr-2 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        {v.plaque} · {v.marque} {v.modele}
                      </div>
                      <div className="text-xs text-gray-500">{v.client_nom}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
          {searchResults.ors?.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Ordres de réparation">
                {searchResults.ors.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={`or-${o.numero}-${o.no_sinistre || ""}-${o.ref_match?.value || ""}-${o.vehicule_label || ""}`}
                    onSelect={() => { setSearchOpen(false); navigate(`/atelier/or/${o.id}`); }}
                    data-testid={`search-result-or-${o.id}`}
                  >
                    <Wrench className="w-4 h-4 mr-2 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        OR {o.numero}
                        {o.no_sinistre && (
                          <span className="ml-2 font-mono text-xs text-violet-700">
                            Sinistre {o.no_sinistre}
                          </span>
                        )}
                        {o.ref_match?.value && o.ref_match.value !== o.no_sinistre && (
                          <span
                            className="ml-2 font-mono text-xs text-violet-700"
                            data-testid={`search-or-refmatch-${o.id}`}
                          >
                            {o.ref_match.label} {o.ref_match.value}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[o.client_nom, o.vehicule_label].filter(Boolean).join(" · ")}
                        {o.devis_numero && ` · Devis ${o.devis_numero}`}
                        {o.facture_numero && ` · Facture ${o.facture_numero}`}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
          {searchResults.factures?.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Factures">
                {searchResults.factures.map((f) => (
                  <CommandItem
                    key={f.id}
                    value={`facture-${f.numero}-${f.plaque || ""}-${f.ref_match?.value || ""}-${f.client_nom || ""}`}
                    onSelect={() => { setSearchOpen(false); navigate(`/facturation/${f.id}`); }}
                    data-testid={`search-result-facture-${f.id}`}
                  >
                    <Receipt className="w-4 h-4 mr-2 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        {f.numero}
                        {f.plaque && <span className="ml-2 font-mono text-xs text-gray-600">{f.plaque}</span>}
                        {f.ref_match?.value && (
                          <span
                            className="ml-2 font-mono text-xs text-violet-700"
                            data-testid={`search-facture-refmatch-${f.id}`}
                          >
                            {f.ref_match.label} {f.ref_match.value}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {f.client_nom}
                        {f.or_numero && ` · OR ${f.or_numero}`}
                        {f.montant_brut != null && ` · ${Number(f.montant_brut).toFixed(2)} CHF`}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
          {searchResults.devis?.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Devis">
                {searchResults.devis.map((d) => (
                  <CommandItem
                    key={d.id}
                    value={`devis-${d.numero}-${d.ref_match?.value || ""}-${d.vehicule_label || ""}-${d.client_nom || ""}`}
                    onSelect={() => { setSearchOpen(false); navigate(`/facturation?tab=devis&devis=${d.id}`); }}
                    data-testid={`search-result-devis-${d.id}`}
                  >
                    <FileText className="w-4 h-4 mr-2 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        Devis {d.numero}
                        {d.ref_match?.value && (
                          <span
                            className="ml-2 font-mono text-xs text-violet-700"
                            data-testid={`search-devis-refmatch-${d.id}`}
                          >
                            {d.ref_match.label} {d.ref_match.value}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[d.client_nom, d.vehicule_label, d.titre].filter(Boolean).join(" · ")}
                        {d.montant_ttc != null && ` · ${Number(d.montant_ttc).toFixed(2)} CHF`}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>

      {/* iter265 — Assistant interne : réservé aux admins */}
      {user?.role === "admin" && <AssistantFab />}
      {/* iter287 — Atelier : aide en ligne (explications seulement) */}
      {user?.role === "atelier" && <AideFab />}
    </div>
  );
}
